import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { ReportingCode, ReportingCodesControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { OptionItemModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.utils';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { keyBy } from 'lodash';
import { pipe, switchMap } from 'rxjs';

export const useReportingCodes = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      reportingCodes: new Array<ReportingCode>(),
      reportingCodesLoaded: false,
    }),
    withProps(() => ({
      _reportingCodesService: inject(ReportingCodesControllerService),
    })),
    withComputed(store => {
      const reportingCodesMap = computed(() => keyBy(store.reportingCodes(), 'id'));

      const reportingCodesOptions = computed((): OptionItemModel[] =>
        store.reportingCodes().map(code => ({ id: code.id!, name: `[${code.environment}] ${code.description}` })),
      );

      return {
        reportingCodesMap,
        reportingCodesOptions,
      };
    }),
    withMethods(store => {
      const loadReportingCodes = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._reportingCodesService.getReportingCodes().pipe(
              tapResponse({
                next: reportingCodes => patchState(store, { reportingCodes, reportingCodesLoaded: true }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification('Failed to load reporting codes', error);
                  patchState(store, { reportingCodesLoaded: true });
                },
              }),
            ),
          ),
        ),
      );
      return {
        loadReportingCodes,
      };
    }),
  );
